/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IBuyboxAddToWishlistViewProps, IBuyboxProductQuantityViewProps } from '@msdyn365-commerce-modules/buybox';
import { IBuyboxCallbacks, IBuyboxState } from '@msdyn365-commerce-modules/buybox';
import { Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';
import { IBuyboxAddToCartViewProps } from './get-add-to-cart';
import { IBuyboxFindInStoreViewProps } from './get-buybox-find-in-store';
import { IFullOrgUnitAvailability } from '../../actions/utilities/full-org-unit-availability';
import { IProductInventoryInformation } from '../../actions/utilities/product-inventory-information';
import { Quantity } from '../../utilities/quantity';
import { IBuyboxResources } from '../../definition-extensions/buybox.ext.props.autogenerated';

export interface IAddToCartContainerProps {
    // Product availability
    isComingSoonProduct: boolean;
    isEcomProductAvailable: boolean;
    isCnCProductAvailable: boolean | undefined;
    isProductAvailable: boolean | undefined;
    isISO: boolean;
    outOfSeasonCheck: boolean;
    /** Whether data is still loading */
    isLoading?: boolean;
    // Delivery selection state
    isHomeDelTileSelected: boolean;
    isCNCTileSelected: boolean;
    // CnC delivery option
    isCnC: boolean;
    // Vendor
    isVendorShipped: boolean;
    // Quantity state
    isQuantityReached: boolean;
    isMaxQuantityReached: boolean;
    quantityError: string | undefined;
    // Auth state
    isAuthenticated: boolean;
    // Components
    quantityComponent: IBuyboxProductQuantityViewProps | undefined;
    addToCart: IBuyboxAddToCartViewProps;
    addToWishlist: IBuyboxAddToWishlistViewProps | undefined;
    findInStore: IBuyboxFindInStoreViewProps | undefined;
    // Callbacks
    callbacks: IBuyboxCallbacks;
    state: IBuyboxState;
    resources: IBuyboxResources;
    // CnC data
    selectedCNCOrgUnitLoc: IFullOrgUnitAvailability | undefined;
    productAvailabilitesForCNC: IProductInventoryInformation[] | undefined;
    // Config
    maxQuantityForCartLineItem: number;
    productData: any;
    productAvailableQuantity: IProductInventoryInformation[] | undefined;
    // Notify me
    onNotifyMeClick: () => void;
}

const AddToCartContainer: React.FC<IAddToCartContainerProps> = ({
    isComingSoonProduct,
    isEcomProductAvailable,
    isCnCProductAvailable,
    isProductAvailable,
    isISO,
    outOfSeasonCheck,
    isLoading,
    isHomeDelTileSelected,
    isCNCTileSelected,
    isCnC,
    isVendorShipped,
    isQuantityReached,
    isMaxQuantityReached,
    quantityError,
    isAuthenticated,
    quantityComponent,
    addToCart,
    addToWishlist,
    findInStore,
    callbacks,
    state,
    resources,
    selectedCNCOrgUnitLoc,
    productAvailabilitesForCNC,
    maxQuantityForCartLineItem,
    productData,
    productAvailableQuantity,
    onNotifyMeClick
}) => {
    if (isComingSoonProduct) {
        return null;
    }

    return (
        <div className='add-to-cart-container'>
            {isEcomProductAvailable || isCnCProductAvailable ? (
                <>
                    {/* Max quantity notification */}
                    {(isQuantityReached || isMaxQuantityReached || quantityError) && (isHomeDelTileSelected || isCNCTileSelected) && (
                        <div className='notification-alert max-quantity-notification notification-alert__yellow'>
                            <div className='notification-alert__message'>
                                <p className='notification-alert__message--paragraph'>You've reached the maximum quantity available.</p>
                            </div>
                        </div>
                    )}

                    {/* Quantity and Add to Cart buttons */}
                    {(isISO || (!outOfSeasonCheck && (isProductAvailable || isISO || isCnCProductAvailable))) && (
                        <Node
                            tag='div'
                            className={classnames(
                                'price-quantity',
                                !isVendorShipped && 'instore-product',
                                'purchase-buttons',
                                isCnC ? 'cnc' : 'no-cnc'
                            )}
                        >
                            {quantityComponent && (
                                <QuantitySelector
                                    quantityComponent={quantityComponent}
                                    callbacks={callbacks}
                                    state={state}
                                    resources={resources}
                                    isCNCTileSelected={isCNCTileSelected}
                                    selectedCNCOrgUnitLoc={selectedCNCOrgUnitLoc}
                                    productAvailabilitesForCNC={productAvailabilitesForCNC}
                                    maxQuantityForCartLineItem={maxQuantityForCartLineItem}
                                    productData={productData}
                                    productAvailableQuantity={productAvailableQuantity}
                                />
                            )}

                            <Node tag='div' className={`buttons-container ${isCnC ? 'cnc' : 'no-cnc'}`}>
                                <div>{((addToCart && !isISO) || isISO || isCnCProductAvailable) && renderAddToCart(addToCart)}</div>
                            </Node>
                        </Node>
                    )}

                    {/* Additional links - check stock and wishlist */}
                    <div className={`additional-links ${isAuthenticated ? '' : 'guest-user'}`}>
                        {!outOfSeasonCheck && findInStore?.checkStock}
                        {!outOfSeasonCheck && isAuthenticated && addToWishlist && renderAddToWishlist(addToWishlist)}
                    </div>
                </>
            ) : null}

            {/* Out of stock section - only show after loading */}
            <Node className='modal-links'>
                {!isLoading && !isEcomProductAvailable && !isCnCProductAvailable && (
                    <div className='out-of-stock'>
                        <p className='out-of-stock__heading'>
                            {!isProductAvailable && !isCnCProductAvailable ? 'Temporarily out of stock online' : ''}
                        </p>
                        {!outOfSeasonCheck && findInStore?.checkStock}
                        <span>or</span>
                        <button className='email-me' onClick={onNotifyMeClick} color='secondary' aria-label='Email me when available'>
                            Email me when available
                        </button>
                        {!outOfSeasonCheck && isAuthenticated && addToWishlist && renderAddToWishlist(addToWishlist)}
                    </div>
                )}
            </Node>
        </div>
    );
};

// Render add to cart button
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { button, ContainerProps } = addToCart;
    return <Node {...ContainerProps}>{button}</Node>;
};

// Render add to wishlist button
const renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            <div>{errorBlock}</div>
            {React.cloneElement(button as React.ReactElement, {
                showInlineLabel: true,
                addedLabelText: 'Added to wishlist',
                redirectOnAddedClick: true,
                wishlistRedirectUrl: '/wishlist'
            })}
        </Node>
    );
};

// Quantity selector sub-component
interface IQuantitySelectorProps {
    quantityComponent: IBuyboxProductQuantityViewProps;
    callbacks: IBuyboxCallbacks;
    state: IBuyboxState;
    resources: IBuyboxResources;
    isCNCTileSelected: boolean;
    selectedCNCOrgUnitLoc: IFullOrgUnitAvailability | undefined;
    productAvailabilitesForCNC: IProductInventoryInformation[] | undefined;
    maxQuantityForCartLineItem: number;
    productData: any;
    productAvailableQuantity: IProductInventoryInformation[] | undefined;
}

const QuantitySelector: React.FC<IQuantitySelectorProps> = ({
    quantityComponent,
    callbacks,
    state,
    resources,
    isCNCTileSelected,
    selectedCNCOrgUnitLoc,
    productAvailabilitesForCNC,
    maxQuantityForCartLineItem,
    productData,
    productAvailableQuantity
}) => {
    const { ContainerProps } = quantityComponent;
    const { quantity } = state;

    const onChange = (newValue: number) => {
        if (callbacks.updateQuantity) {
            callbacks.updateQuantity(newValue);
        }
    };

    // Calculate CnC inventory
    const cncInventory = productAvailabilitesForCNC?.find(
        inventory => inventory.InventLocationId === selectedCNCOrgUnitLoc?.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId
    );

    // Get product inventory
    let productInventory = productAvailableQuantity?.find(
        inventory => inventory.ProductAvailableQuantity.ProductId === productData?.RecordId
    );

    if (isCNCTileSelected && cncInventory) {
        productInventory = cncInventory;
    }

    let maxQuantity = maxQuantityForCartLineItem || 10;
    if (
        productInventory?.ProductAvailableQuantity.AvailableQuantity &&
        productInventory?.ProductAvailableQuantity.AvailableQuantity < maxQuantity
    ) {
        maxQuantity = productInventory?.ProductAvailableQuantity.AvailableQuantity;
    }

    return (
        <Node {...ContainerProps}>
            <Quantity
                id='ms-buybox__product-quantity-input'
                max={maxQuantity || 10}
                currentCount={quantity}
                onChange={onChange}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
            />
        </Node>
    );
};

export default AddToCartContainer;
export { renderAddToCart, renderAddToWishlist };
